import { type CSSProperties, type FC, useState } from 'react';
import cx from 'classnames';

import { IconArrowLeft_16 } from 'src/assets/icons/16x16/IconArrowLeft_16';
import { IconArrowRight_16 } from 'src/assets/icons/16x16/IconArrowRight_16';
import { CUSTOM_ICON_BUTTON_SIZE, CustomIconButton } from 'src/components/CustomIconButton';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import {
  PROJECT_TEMPLATE_CARD_TYPE,
  ProjectTemplateCard,
  // @ts-expect-error - ProjectTemplateCard is not typed yet
} from 'src/components/ProjectTemplateCard';
import {
  type RecentlyUsedTemplateCardData,
  useRecentlyUsedTemplatesCards,
} from 'src/services/templates/useRecentlyUsedTemplatesCards';

import { WIDGET_ACTION, type WidgetActionHandler } from '../../lib';

import styles from './styles.module.scss';
import { useSupportScrolling } from './useSupportScrolling';

interface RecentlyUsedTemplatesProps {
  className?: string;
  onWidgetAction: WidgetActionHandler;
  wideMargins?: boolean;
  disabled?: boolean;
}

export const RecentlyUsedTemplates: FC<RecentlyUsedTemplatesProps> = ({
  className,
  onWidgetAction,
  wideMargins,
  disabled,
}) => {
  const [cardsElement, setCardsElement] = useState<HTMLElement | null>(null);

  const cards = useRecentlyUsedTemplatesCards();

  const { scrollState, handleScrollLeft, handleScrollRight } = useSupportScrolling(
    cardsElement,
    cards.length
  );

  const handleCardSelect = (card: RecentlyUsedTemplateCardData) => {
    onWidgetAction(WIDGET_ACTION.createRecentProject, { recentlyUsedTemplateCardData: card });
  };

  if (cards.length === 0) {
    return null;
  }

  return (
    <div
      className={cx(styles.root, className)}
      /* expand over "paddings" of the whole "widget" area */
      style={{ '--side-spacing': wideMargins ? '32px' : '24px' } as CSSProperties}
    >
      <header className={styles.header}>
        <CustomTypography
          className={styles.title}
          variant={TYPOGRAPHY_VARIANT.regular14}
        >
          Recently used templates
        </CustomTypography>

        <CustomIconButton
          className={styles.scrollButton}
          onClick={handleScrollLeft}
          disabled={!scrollState.canScrollLeft}
          size={CUSTOM_ICON_BUTTON_SIZE.small}
        >
          <IconArrowLeft_16 />
        </CustomIconButton>

        <CustomIconButton
          className={styles.scrollButton}
          onClick={handleScrollRight}
          disabled={!scrollState.canScrollRight}
          size={CUSTOM_ICON_BUTTON_SIZE.small}
        >
          <IconArrowRight_16 />
        </CustomIconButton>
      </header>

      <main className={styles.cardsWrapper}>
        <div
          className={styles.cards}
          ref={setCardsElement}
        >
          {cards.map((card) => (
            <ProjectTemplateCard
              className={styles.card}
              key={card.id}
              cardType={PROJECT_TEMPLATE_CARD_TYPE.medium}
              projectType={card.projectType}
              generationTool={card.generationTool}
              customToolData={card.customToolData}
              subTemplateData={card.subTemplateData}
              onSelect={() => handleCardSelect(card)}
              disabled={disabled}
            />
          ))}
        </div>
      </main>
    </div>
  );
};
